// Ask URL-sync — the pure decision core behind AskRoute's /ask ↔ /ask/:chatId reconciliation.
//
// The URL is the source of truth for which thread is shown. AskRoute drives the hook FROM the `:chatId`
// path param, and reflects a server-minted chat id BACK into the URL (the mint on the first persisted turn
// of a bare /ask thread). The fiddly part is not looping: a navigate WE performed must not be mistaken for
// a browser back/forward the USER performed. That distinction is a `loaded` ref AskRoute keeps — the last
// chatId it reconciled. These reducers are pure so the whole dance is testable without a DOM (the SPA has
// no React Testing Library; see useIsMobile / map-list-params for the same pure-core pattern).

export type AskUrlAction =
  | { type: 'none' }
  | { type: 'load'; chatId: string } // the URL names a thread the hook isn't showing yet → load it
  | { type: 'reset' } // the URL went to bare /ask → start an empty thread
  | { type: 'sync-url'; chatId: string }; // the hook has an id the URL doesn't reflect → navigate there

/**
 * URL → thread. React to the URL's `:chatId`. `loaded` is the chatId AskRoute last reconciled, so a nav we
 * just performed (which set `loaded` first) is a no-op rather than a redundant reload. A deep-link or a
 * browser back/forward to a different chatId loads it; bare /ask (no chatId) resets to an empty thread.
 * Mount on bare /ask (loaded===null, chatId===undefined) is a no-op — the hook already starts empty.
 */
export function reconcileUrlToThread(urlChatId: string | undefined, loaded: string | null): AskUrlAction {
  const target = urlChatId ?? null;
  if (target === loaded) return { type: 'none' };
  if (target) return { type: 'load', chatId: target };
  return { type: 'reset' };
}

/**
 * Thread → URL. Reflect the hook's active thread id when the URL doesn't carry it — i.e. the server minted
 * an id for a bare /ask thread (or a load resolved to an id the URL lacks). Never fires while the hook has
 * no active thread, and never when the URL already matches, so it cannot fight reconcileUrlToThread.
 */
export function reconcileThreadToUrl(activeChatId: string | null, urlChatId: string | undefined): AskUrlAction {
  if (activeChatId && activeChatId !== (urlChatId ?? null)) return { type: 'sync-url', chatId: activeChatId };
  return { type: 'none' };
}
